import isDeclaredFileSelected from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sidebars/DocumentTemplateUploadSidebar/FormData/constraint/isDeclaredFileSelected';
import isFilledWhenCreating from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sidebars/DocumentTemplateUploadSidebar/FormData/constraint/isFilledWhenCreating';
import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';

export type DocumentTemplateUploadFormArgs = {
	mode: 'create' | 'version';
	name: string;
	documentTypeId: number | null;
	description: string;
	file: File | null;
	isFileDeclared: boolean;
	reevaluationSectionKeys?: readonly string[];
};

/**
 * One shape for both deposit modes: `create` needs `name`/`documentTypeId`/`description` in addition to
 * `file`, `version` needs only `file` — `mode` drives which fields the custom constraints require
 * (`isFilledWhenCreating`). `isFileDeclared` is never sent to the server; it only tracks whether the
 * local preview (`DescribeDocumentTemplateFile`) last settled on `declared`, which is what makes
 * `file` valid (`isDeclaredFileSelected`). `ignoreNullValue` is deliberately absent: with it, `file:
 * null` would be treated as valid and its constraint would never even run (`guides/forms.md`).
 */
@ValidatedClass()
class DocumentTemplateUploadFormData extends AbstractValidatedClass<DocumentTemplateUploadFormArgs> {
	public readonly mode: 'create' | 'version';

	@constraint(Joi.string().max(180).allow(''), () => 'Le nom ne peut pas dépasser 180 caractères.')
	@constraint(isFilledWhenCreating, () => 'Le nom du gabarit est obligatoire')
	public readonly name: string;

	@constraint(isFilledWhenCreating, () => 'Le type de document est obligatoire')
	public readonly documentTypeId: number | null;

	@constraint(
		Joi.string().max(2000).allow(''),
		() => 'La description ne peut pas dépasser 2000 caractères.',
	)
	public readonly description: string;

	@constraint(isDeclaredFileSelected, () => 'Choisissez un fichier .docx')
	public readonly file: File | null;

	public readonly isFileDeclared: boolean;

	public readonly reevaluationSectionKeys: readonly string[];

	constructor(data: DocumentTemplateUploadFormArgs) {
		super(data);
		this.mode = data.mode;
		this.name = data.name;
		this.documentTypeId = data.documentTypeId;
		this.description = data.description;
		this.file = data.file;
		this.isFileDeclared = data.isFileDeclared;
		this.reevaluationSectionKeys = data.reevaluationSectionKeys ?? [];
		Object.freeze(this);
	}

	static createEmpty(mode: 'create' | 'version'): DocumentTemplateUploadFormData {
		return new DocumentTemplateUploadFormData({
			mode,
			name: '',
			documentTypeId: null,
			description: '',
			file: null,
			isFileDeclared: false,
			reevaluationSectionKeys: [],
		});
	}

	setName(name: string): this {
		return this.cloneWith({ name } as Partial<DocumentTemplateUploadFormArgs>);
	}

	setDocumentTypeId(documentTypeId: number | null): this {
		return this.cloneWith({ documentTypeId } as Partial<DocumentTemplateUploadFormArgs>);
	}

	setDescription(description: string): this {
		return this.cloneWith({ description } as Partial<DocumentTemplateUploadFormArgs>);
	}

	/**
	 * Selecting a new file always resets `isFileDeclared` and the marked sections: the previous preview
	 * no longer describes it.
	 */
	setFile(file: File | null): this {
		return this.cloneWith({
			file,
			isFileDeclared: false,
			reevaluationSectionKeys: [],
		} as Partial<DocumentTemplateUploadFormArgs>);
	}

	setReevaluationSectionKeys(reevaluationSectionKeys: readonly string[]): this {
		return this.cloneWith({ reevaluationSectionKeys } as Partial<DocumentTemplateUploadFormArgs>);
	}

	toggleReevaluationSection(key: string): this {
		const reevaluationSectionKeys = this.reevaluationSectionKeys.includes(key)
			? this.reevaluationSectionKeys.filter((current) => current !== key)
			: [...this.reevaluationSectionKeys, key];

		return this.cloneWith({ reevaluationSectionKeys } as Partial<DocumentTemplateUploadFormArgs>);
	}

	setIsFileDeclared(isFileDeclared: boolean): this {
		return this.cloneWith({ isFileDeclared } as Partial<DocumentTemplateUploadFormArgs>);
	}
}

export default DocumentTemplateUploadFormData;
